/* eslint-disable @eslint-react/dom/no-dangerously-set-innerhtml */
import classNames from 'classnames';
import { type CSSProperties, type MouseEvent, useEffect, useState } from 'react';
import tinycolor from 'tinycolor2';
import { useLocation } from 'wouter';

import { getParentElement } from '../../lib/isInElement';

import { useSubscriptions } from '../../contexts/SiteContext';
import HeaderAd from '../ads/HeaderAd';
import SubscriptionForm from '../forms/SubscriptionForm';

import styles from '../../styles/documents/newsletter-document.module.css';

/**
 * Retourne le CSS de tous les <style> du <head> qui sont réellement appliqués par un
 * navigateur. Les commentaires HTML sont retirés au préalable, ce qui exclut du même
 * coup les blocs conditionnels masqués (<!--[if mso]><style>...</style><![endif]-->)
 * tout en conservant ceux qui sont révélés (<!--[if !mso]><!--><style>...</style><!--<![endif]-->).
 */
function getHeadStyles(html: string | null): string | null {
    const [, head = null] = (html || '').match(/<head[^>]*>([\s\S]*)<\/head>/) || [];
    if (head === null) {
        return null;
    }
    const visibleHead = head.replace(/<!--[\s\S]*?-->/g, '');
    const styles = [...visibleHead.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/g)]
        .map(([, css]) => css.trim())
        .filter((css) => css.length > 0);
    return styles.length > 0 ? styles.join('\n') : null;
}

interface NewsletterDocumentProps extends Partial<NewsletterDocument> {
    // Ad space at the top of the page (`HeaderAd`), on the background of the page
    withHeaderAd?: boolean;
    className?: string | null;
}

function NewsletterDocument({
    withHeaderAd = false,
    id = null,
    slug = null,
    title = null,
    html = null,
    metadata = null,
    className = null,
}: NewsletterDocumentProps) {
    const [, setLocation] = useLocation();
    const subscriptions = useSubscriptions();
    const { subscription: subscriptionHandle = null } = metadata || {};
    const subscription =
        subscriptionHandle !== null
            ? subscriptions.find(({ handle }) => handle === subscriptionHandle) || null
            : null;
    const [, body = null] = (html || '').match(/<body[^>]*>([\s\S.]*)<\/body>/) || [];
    const style = getHeadStyles(html);
    const [, , backgroundColor = null] =
        (html || '').match(/class="body([^>]+)background-color:((#[0-9a-f]{3,6})|transparent)/i) ||
        [];
    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        const link = getParentElement(event.target as HTMLElement, 'a');
        const url = link !== null ? link.getAttribute('href') : null;
        if (url !== null && url.match(/urbania\.(ca|fr)/gi) !== null) {
            event.preventDefault();
            setLocation(url.replace(/^http?s:\/\/([^/]+)/, ''));
        }
    };
    const isDark =
        backgroundColor !== 'transparent' &&
        backgroundColor !== null &&
        tinycolor(backgroundColor).isDark();

    const [parsedBody, setParsedBody] = useState<string | null>(null);
    useEffect(() => {
        const div = document.createElement('div');
        const cleanBody = (body || '').replace(/\\"/g, '"'); // Remove escaped quotes
        div.innerHTML = cleanBody.replace(/<body/, '<div').replace(/<\/body>/, '</div>');
        const rows = [...div.getElementsByClassName('row')] as HTMLElement[];
        rows.forEach((row) => {
            const text = row.innerText.toLowerCase();
            const links = [...row.getElementsByTagName('a')];
            const shouldRemove =
                links.findIndex((a) => {
                    const url = a.getAttribute('href') || null;
                    return (
                        url !== null &&
                        (url.indexOf('desabonner') !== -1 ||
                            url.indexOf('unsubscribe') !== -1 ||
                            url.indexOf('compte/abonnements') !== -1)
                    );
                }) !== -1 ||
                text.indexOf('lisez ce courriel dans un navigateur') !== -1 ||
                text.indexOf('suivez-nous quand même') !== -1 ||
                text.indexOf('suivez-nous sur les') !== -1;
            if (shouldRemove) {
                if (row.parentNode !== null) {
                    row.parentNode.removeChild(row);
                }
                return;
            }
        });
        setParsedBody(div.innerHTML);
    }, [body]);

    return (
        <div
            className={classNames([styles.container, className])}
            style={
                backgroundColor !== null && backgroundColor !== 'transparent'
                    ? ({
                          '--page-background': backgroundColor,
                      } as CSSProperties)
                    : null
            }
            data-theme={isDark ? 'dark' : 'light'}
        >
            {withHeaderAd ? <HeaderAd /> : null}
            <div className={styles.header}>
                <div className={styles.line}>
                    <div className={styles.label}>Objet</div>
                    <div className={styles.value}>{title}</div>
                </div>
                {subscription !== null ? (
                    <div className={classNames([styles.line])}>
                        <div className={styles.label}>De</div>
                        <div className={styles.value}>{subscription.name}</div>
                    </div>
                ) : null}
                <div className={classNames([styles.line, styles.recipient])}>
                    <div className={styles.label}>À</div>
                    <div className={styles.value}>
                        <SubscriptionForm
                            subscription={subscriptionHandle}
                            source={`newsletter-page-${slug}`}
                            alwaysHorizontal
                            className={styles.form}
                        />
                    </div>
                </div>
            </div>
            <div id={`newsletter-${id}`}>
                <style
                    type="text/css"
                    dangerouslySetInnerHTML={{
                        __html: `#newsletter-${id} { ${style || ''} }`,
                    }}
                />
                <div
                    dangerouslySetInnerHTML={{
                        __html: parsedBody || '',
                    }}
                    onClick={onClick}
                />
            </div>
        </div>
    );
}

export default NewsletterDocument;
